import React from "react";
import { Container, Grid, Typography, IconButton, Box, Divider, Stack } from "@mui/material";
import { Facebook, LinkedIn, Instagram, GitHub } from "@mui/icons-material";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import { Link as ScrollLink } from "react-scroll";
import { brand } from "../theme";
import zealous from "../Images/Zealous.png";

// Official Google Maps embed for the Zealous head office (reliable, no API key).
// NOTE: the !4v version token differs from the Contact-section map so the two
// iframes have unique URLs — Google can render a second identical embed blank.
const MAP_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3907.3296799339414!2d78.13951807459888!3d11.671031488537412!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3babf04680edbe0f%3A0xcb56c889044c2542!2sZealous%20Academy%20of%20Career%20Training!5e0!3m2!1sen!2sin!4v1782976059777!5m2!1sen!2sin";

const quickLinks = [
  { to: "home-section", label: "Home" },
  { to: "about-section", label: "About" },
  { to: "services-section", label: "Services" },
  { to: "training-section", label: "Technology" },
  { to: "products-section", label: "Products" },
];

const exploreLinks = [
  { to: "portfolio-section", label: "Training Portfolio" },
  { to: "casestudies-section", label: "Case Studies" },
  { to: "roadmap-section", label: "Roadmap" },
  { to: "campus-section", label: "Campuses" },
  { to: "contacts-section", label: "Contact" },
];

const socials = [
  { icon: <Facebook />, href: "https://www.facebook.com/razzaksr/", label: "Facebook" },
  { icon: <LinkedIn />, href: "https://www.linkedin.com/in/zealous-academy-of-career-training-9927bb117/?originalSubdomain=in", label: "LinkedIn" },
  { icon: <Instagram />, href: "https://www.instagram.com/zealous_tech/", label: "Instagram" },
  { icon: <GitHub />, href: "https://github.com/zealoustechcorp", label: "GitHub" },
];

const linkStyle = {
  color: "rgba(255,255,255,0.72)",
  textDecoration: "none",
  cursor: "pointer",
  fontSize: "0.9rem",
  display: "inline-block",
  padding: "5px 0",
  transition: "color 0.2s ease, transform 0.2s ease",
};

const FooterLink = ({ to, label }) => (
  <Box component="li" sx={{ listStyle: "none" }}>
    <ScrollLink
      to={to}
      smooth={true}
      duration={700}
      style={linkStyle}
      onMouseOver={(e) => {
        e.currentTarget.style.color = "#fff";
        e.currentTarget.style.transform = "translateX(4px)";
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.color = "rgba(255,255,255,0.72)";
        e.currentTarget.style.transform = "none";
      }}
    >
      {label}
    </ScrollLink>
  </Box>
);

const Footer = () => {
  return (
    <footer style={{ background: "#0b1f33", color: "#fff" }}>
      <Box sx={{ py: { xs: 5, md: 7 } }}>
        <Container>
          <Grid container spacing={4}>
            {/* Brand */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2.5 }}>
                <img src={zealous} alt="Zealous Tech Corp" style={{ maxHeight: 76, maxWidth: "100%" }} />
              </Box>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.9rem", mb: 2.5, maxWidth: 340 }}>
                Empowering institutions through technology, innovation and workforce
                transformation — bridging academia and industry through practical
                technology enablement.
              </Typography>
              <Stack direction="row" spacing={1}>
                {socials.map((s) => (
                  <IconButton
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener"
                    aria-label={s.label}
                    sx={{
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.08)",
                      transition: "all 0.2s ease",
                      "&:hover": { bgcolor: brand.orange, transform: "translateY(-3px)" },
                    }}
                  >
                    {s.icon}
                  </IconButton>
                ))}
              </Stack>
            </Grid>

            {/* Quick links */}
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: "1rem" }}>
                Company
              </Typography>
              <Box component="ul" sx={{ p: 0, m: 0 }}>
                {quickLinks.map((l) => (
                  <FooterLink key={l.to} {...l} />
                ))}
              </Box>
            </Grid>

            {/* Explore */}
            <Grid item xs={6} sm={4} md={2}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: "1rem" }}>
                Explore
              </Typography>
              <Box component="ul" sx={{ p: 0, m: 0 }}>
                {exploreLinks.map((l) => (
                  <FooterLink key={l.to} {...l} />
                ))}
              </Box>
            </Grid>

            {/* Contact */}
            <Grid item xs={12} sm={4} md={4}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: "1rem" }}>
                Reach Us
              </Typography>
              <Stack spacing={1.5}>
                {/* Map view in place of the address text */}
                <Box
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.14)",
                    height: 150,
                  }}
                >
                  <Box
                    component="iframe"
                    title="Zealous Tech Corp — Salem Office"
                    src={MAP_SRC}
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    sx={{ border: 0, width: "100%", height: "100%", minHeight: 150, display: "block" }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
                  <CallIcon sx={{ color: brand.orange, fontSize: 18, flexShrink: 0 }} />
                  <Typography component="a" href="tel:+919597334782" sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.82rem", textDecoration: "none", "&:hover": { color: "#fff" } }}>
                    +91 95973 34782
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
                  <EmailIcon sx={{ color: brand.orange, fontSize: 18, flexShrink: 0 }} />
                  <Typography component="a" href="mailto:reachus@zealoustechcorp.com" sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.82rem", textDecoration: "none", "&:hover": { color: "#fff" } }}>
                    reachus@zealoustechcorp.com
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.12)", my: 4 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>
              &copy; {new Date().getFullYear()} Zealous Tech Corp. All rights reserved.
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>
              Government of India · MSME Registered Enterprise
            </Typography>
          </Box>
        </Container>
      </Box>
    </footer>
  );
};

export default Footer;
